'use client';

import { getPlans, getSubscription } from '@/api/usage';
import AccessRestrictedModal from '@/app/components/entitlement/AccessRestrictedModal';
import PlansComparison, { type PlanTier } from '@/app/components/settings/PlansComparison';
import UpgradePlanModal from '@/app/components/settings/UpgradePlanModal';
import {
  buildLocalizedPlansComparison,
  buildPlanTierFromSubscription,
  formatPlanAmount,
  formatPlanPeriod,
} from '@/app/components/settings/plans-data.utils';
import {
  pollClientEngagementConfig,
  refreshSubscriptionQueries,
} from '@/app/components/settings/subscription-cache.utils';
import Pill from '@/app/components/ui/Pill';
import { Button } from '@/app/components/ui/Button';
import { Card } from '@/app/components/ui/Card';
import Modal from '@/app/components/ui/Modal';
import Spinner from '@/app/components/ui/Spinner';
import { cn } from '@/app/lib/utils';
import { planStatusBadgeClassNames } from '@/constants/color';
import type { EntitlementGateResult, EntitlementGateVariant } from '@/types/entitlement';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Box, LockKeyhole, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';
import { useMemo, useState } from 'react';
import { useUser } from '@/app/context/user';

type EntitlementGateModalProps = {
  open: boolean;
  variant: EntitlementGateVariant | null;
  gateResult?: EntitlementGateResult | null;
  onOpenChange: (open: boolean) => void;
  onViewPlans: () => void;
  zIndex?: number;
};

function formatRenewalDate(value?: string | null) {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

function getResourceLabel(
  t: ReturnType<typeof useTranslations<'settings.plansPage'>>,
  limitResource?: string,
  resource?: string
) {
  const value = limitResource || resource;

  switch (value) {
    case 'user':
      return t('entitlementGate.resources.users');
    case 'mailbox':
      return t('entitlementGate.resources.mailboxes');
    case 'linkedin':
      return t('entitlementGate.resources.linkedInAccounts');
    case 'imessage_phone_line':
      return t('entitlementGate.resources.iMessageLines');
    case 'campaign':
      return t('entitlementGate.resources.campaigns');
    case 'task':
      return t('entitlementGate.resources.tasks');
    case 'integration':
      return t('entitlementGate.resources.integrations');
    case 'export':
      return t('entitlementGate.resources.exports');
    case 'target_list':
      return t('entitlementGate.resources.targetLists');
    default:
      return t('entitlementGate.resources.thisFeature');
  }
}

export default function EntitlementGateModal({
  open,
  variant,
  gateResult,
  onOpenChange,
  onViewPlans,
  zIndex,
}: EntitlementGateModalProps) {
  const { clientId } = useParams<{ clientId: string }>();
  const tPlans = useTranslations('settings.plansPage');
  const tPlanStatus = useTranslations('common.planStatus');
  const queryClient = useQueryClient();
  const { setClient } = useUser();
  const [selectedPlan, setSelectedPlan] = useState<PlanTier | null>(null);
  const blockedGateResult = gateResult && !gateResult.allowed ? gateResult : null;
  const isAccessRestricted = blockedGateResult?.reason === 'user_permission_denied';

  const plansQuery = useQuery({
    queryKey: ['settings-plans', clientId],
    queryFn: () => getPlans(clientId),
    enabled: open && !isAccessRestricted && !!clientId,
  });

  const subscriptionQuery = useQuery({
    queryKey: ['settings-plans-subscription', clientId],
    queryFn: () => getSubscription(clientId || ''),
    enabled: open && !isAccessRestricted && !!clientId,
  });

  const subscription = subscriptionQuery.data;
  const comparisonData =
    plansQuery.data && subscription
      ? buildLocalizedPlansComparison(plansQuery.data, subscription, tPlans, {
          includeFreePlanColumn: false,
        })
      : null;

  const currentPlan = useMemo(
    () => (subscription ? buildPlanTierFromSubscription(subscription) : null),
    [subscription]
  );
  const limitLabel = useMemo(
    () => getResourceLabel(tPlans, blockedGateResult?.limitResource, blockedGateResult?.resource),
    [blockedGateResult?.limitResource, blockedGateResult?.resource, tPlans]
  );

  const content = useMemo(() => {
    if (!subscription) {
      return {
        icon: LockKeyhole,
        iconWrapperClassName: 'bg-success/10',
        iconClassName: 'text-success',
        title: tPlans('entitlementGate.defaultTitle'),
        description: tPlans('entitlementGate.defaultDescription'),
        currentPlanStatus: tPlans('entitlementGate.status.active'),
      };
    }

    const totalCredits =
      subscription.last_cycle_credit_usage?.total_credits ?? subscription.plan.total_credits;
    const isFreePlan = subscription.plan.type === 'free_plan';

    if (blockedGateResult?.reason === 'limit_reached') {
      return {
        icon: AlertTriangle,
        iconWrapperClassName: 'bg-destructive/10',
        iconClassName: 'text-destructive',
        title: tPlans('entitlementGate.limitReachedTitle'),
        description: tPlans('entitlementGate.limitReachedDescription', {
          resource: limitLabel,
          planName: subscription.plan.name,
        }),
        currentPlanStatus: subscription.status || 'active',
      };
    }

    if (variant === 'limit_reached') {
      return {
        icon: AlertTriangle,
        iconWrapperClassName: 'bg-destructive/10',
        iconClassName: 'text-destructive',
        title: isFreePlan
          ? tPlans('entitlementGate.freeCreditsUsedTitle', {
              credits: totalCredits.toLocaleString(),
            })
          : tPlans('entitlementGate.creditsUsedTitle', {
              credits: totalCredits.toLocaleString(),
            }),
        description: isFreePlan
          ? tPlans('entitlementGate.freeCreditsUsedDescription', {
              credits: totalCredits.toLocaleString(),
            })
          : tPlans('entitlementGate.creditsUsedDescription', {
              credits: totalCredits.toLocaleString(),
              planName: subscription.plan.name,
            }),
        currentPlanStatus: isFreePlan
          ? tPlans('entitlementGate.status.expired')
          : subscription.status || 'active',
        currentPlanStatusClassName: isFreePlan ? 'bg-destructive/10 text-destructive' : undefined,
      };
    }

    return {
      icon: LockKeyhole,
      iconWrapperClassName: 'bg-success/10',
      iconClassName: 'text-success',
      title: tPlans('entitlementGate.featureLockedTitle'),
      description: tPlans('entitlementGate.featureLockedDescription'),
      currentPlanStatus: subscription.status || 'active',
    };
  }, [blockedGateResult?.reason, limitLabel, subscription, tPlans, variant]);

  const currentPlanMeta = useMemo(() => {
    if (!subscription) return '';
    return subscription.next_renewal_date
      ? tPlans('entitlementGate.renewsOn', {
          date: formatRenewalDate(subscription.next_renewal_date),
        })
      : '';
  }, [subscription, tPlans]);

  const currentPlanSubtitle = useMemo(() => {
    if (!subscription) return '$0';
    const amount = formatPlanAmount(subscription.plan.amount);
    const period = formatPlanPeriod(subscription.plan.recurring_type);
    return `${amount}${period}`;
  }, [subscription]);

  const compareDialogOpen = open && !selectedPlan;
  const upgradeDialogOpen = open && !!selectedPlan;
  const showCurrentPlanCard = variant === 'limit_reached';

  if (isAccessRestricted) {
    return (
      <AccessRestrictedModal
        open={open}
        gateResult={gateResult}
        zIndex={zIndex}
        onOpenChange={nextOpen => {
          if (!nextOpen) {
            setSelectedPlan(null);
          }
          onOpenChange(nextOpen);
        }}
      />
    );
  }

  return (
    <>
      <Modal
        isOpen={compareDialogOpen}
        onOpenChange={nextOpen => {
          if (!nextOpen) {
            setSelectedPlan(null);
            onOpenChange(false);
          }
        }}
        zIndex={zIndex}
        bodyClassName={cn(
          'flex max-h-[min(880px,calc(100dvh-2rem))] flex-col gap-0 overflow-hidden rounded-[4px] border border-border bg-background p-0 shadow-[0px_16px_32px_-12px_rgba(14,18,27,0.1)]',
          'max-w-[880px] sm:max-w-[880px]',
          'w-[calc(100vw-32px)]'
        )}
      >
        <div className="relative flex shrink-0 flex-col items-center px-5 pt-8 pb-0 text-center">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-4 top-4 h-6 w-6 rounded-[4px] p-0 text-muted-foreground"
            onClick={() => {
              setSelectedPlan(null);
              onOpenChange(false);
            }}
          >
            <X className="h-5 w-5" />
            <span className="sr-only">Close</span>
          </Button>
          {plansQuery.isLoading ||
          subscriptionQuery.isLoading ||
          !subscription ||
          !comparisonData ||
          !currentPlan ? (
            <>
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full"></div>

              <h2 className="max-w-[640px] text-center text-[24px] font-medium leading-8 tracking-[-0.36px] text-foreground"></h2>

              <p className="mt-1 max-w-[484px] text-center text-[16px] leading-6 tracking-[-0.176px] text-muted-foreground"></p>
            </>
          ) : (
            <>
              <div
                className={cn(
                  'mb-4 flex h-12 w-12 items-center justify-center rounded-full',
                  content.iconWrapperClassName
                )}
              >
                <content.icon className={cn('h-6 w-6', content.iconClassName)} />
              </div>

              <h2 className="max-w-[640px] text-center text-[24px] font-medium leading-8 tracking-[-0.36px] text-foreground">
                {content.title}
              </h2>

              <p className="mt-1 max-w-[484px] text-center text-[16px] leading-6 tracking-[-0.176px] text-muted-foreground">
                {content.description}
              </p>
            </>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-8">
          {plansQuery.isLoading ||
          subscriptionQuery.isLoading ||
          !subscription ||
          !comparisonData ||
          !currentPlan ? (
            <div className="flex min-h-[320px] items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <div className="flex flex-col gap-[26px]">
              {showCurrentPlanCard ? (
                <Card className="overflow-hidden rounded-[4px] border border-border bg-background shadow-none">
                  <div className="flex items-center gap-3 px-4 py-4">
                    <div className="flex shrink-0 items-center justify-center rounded-full bg-primary/10 p-[10px] text-information-base">
                      <Box className="size-5" />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1">
                        <p className="text-[14px] font-medium leading-5 tracking-[-0.084px] text-foreground">
                          {subscription.plan.name}
                        </p>
                        <Pill
                          className={cn(
                            'rounded-[4px] border-0 px-1 py-0.5 text-[12px] font-medium leading-4',
                            content.currentPlanStatusClassName ||
                              planStatusBadgeClassNames[content.currentPlanStatus] ||
                              planStatusBadgeClassNames.inactive
                          )}
                        >
                          {content.currentPlanStatusClassName
                            ? content.currentPlanStatus
                            : tPlanStatus(content.currentPlanStatus)}
                        </Pill>
                      </div>

                      <div className="mt-1 flex items-start gap-1 text-[12px] font-medium leading-4">
                        <span className="text-muted-foreground">
                          {currentPlanSubtitle.split('/')[0]}
                          {currentPlanSubtitle.includes('/') ? (
                            <span className="text-muted-foreground">
                              /{currentPlanSubtitle.split('/')[1]}
                            </span>
                          ) : null}
                        </span>
                        {currentPlanMeta ? (
                          <>
                            <span className="mt-0.5 h-3 w-px bg-border" aria-hidden="true" />
                            <span className="text-muted-foreground">{currentPlanMeta}</span>
                          </>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </Card>
              ) : null}

              <PlansComparison
                className="gap-4"
                plans={comparisonData.plans}
                comparisonRows={comparisonData.comparisonRows}
                customSetupNote={comparisonData.customSetupNote}
                onPlanAction={plan => {
                  if (plan.isCustom || plan.ctaLabel === tPlans('talkToUs')) {
                    onViewPlans();
                    return;
                  }

                  setSelectedPlan(plan);
                }}
              />
            </div>
          )}
        </div>
      </Modal>

      {selectedPlan && currentPlan && clientId ? (
        <UpgradePlanModal
          clientId={clientId}
          currentPlan={currentPlan}
          selectedPlan={selectedPlan}
          isDowngrade={selectedPlan.sequence < currentPlan.sequence}
          isOpen={upgradeDialogOpen}
          zIndex={zIndex}
          onOpenChange={nextOpen => {
            if (!nextOpen) {
              setSelectedPlan(null);
            }
          }}
          onSuccess={() => {
            const expectedPlanId = selectedPlan.id;

            setSelectedPlan(null);
            onOpenChange(false);
            void refreshSubscriptionQueries(queryClient, clientId, {
              expectedPlanId,
            });
            if (currentPlan.type === 'free_plan') {
              void pollClientEngagementConfig(queryClient, clientId).then(setClient);
            }
          }}
        />
      ) : null}
    </>
  );
}
